import { useEffect, useRef, useState, useCallback } from "react";

// Phase 29 — Auto-Save drafts every 30 seconds into localStorage.
// Use case: Recipe Builder, Menu Editor, Cost Profile.
//
// Keying schema:
//   `studioops_draft_${scope}_${id || "new"}`
//
// Usage:
//   const { draftFound, loadDraft, clearDraft, lastSavedAt } =
//     useAutoSaveDraft({ scope: "recipe-builder", id: editing?.id, value: form, enabled: open });

const PREFIX = "studioops_draft_";
const INTERVAL_MS = 30_000;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

function storageKey(scope, id) {
  return `${PREFIX}${scope}_${id || "new"}`;
}

export default function useAutoSaveDraft({ scope, id, value, enabled = true }) {
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [draftFound, setDraftFound] = useState(null); // {value, savedAt} or null
  const valueRef = useRef(value);
  valueRef.current = value;

  const key = storageKey(scope, id);

  // On mount/open: check for an existing draft
  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) { setDraftFound(null); return; }
      const parsed = JSON.parse(raw);
      if (!parsed?.savedAt) { setDraftFound(null); return; }
      if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
        window.localStorage.removeItem(key);
        setDraftFound(null);
        return;
      }
      setDraftFound(parsed);
    } catch {
      setDraftFound(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // Interval: save every 30 s
  useEffect(() => {
    if (!enabled) return;
    const iv = setInterval(() => {
      try {
        const v = valueRef.current;
        if (v == null) return;
        const payload = { value: v, savedAt: Date.now() };
        window.localStorage.setItem(key, JSON.stringify(payload));
        setLastSavedAt(payload.savedAt);
      } catch {
        // localStorage might be full or disabled
      }
    }, INTERVAL_MS);
    return () => clearInterval(iv);
  }, [key, enabled]);

  const loadDraft = useCallback(() => draftFound?.value ?? null, [draftFound]);

  const clearDraft = useCallback(() => {
    try { window.localStorage.removeItem(key); } catch {}
    setDraftFound(null);
    setLastSavedAt(null);
  }, [key]);

  const dismissDraft = useCallback(() => {
    // Hide the banner but keep the value (could be restored later via reload)
    setDraftFound(null);
  }, []);

  return { draftFound, loadDraft, clearDraft, dismissDraft, lastSavedAt };
}
